// CRM domain model — pipeline stages, call outcomes, rating scales.
// Shared vocabulary between the focus queue, the table view, and the
// outcome flow. Must mirror the backend where noted.

export const STAGES = ['New', 'Calling', 'Contacted', 'Booked', 'Handed Over', 'Disqualified', 'Existing Customer', 'Wrong Contact'] as const
export type Stage = (typeof STAGES)[number]

// Per-call-attempt result, as tracked by the admin analytics (day trace, lead
// trail, hour columns). Distinct from Stage (a lead-level state) — see
// shared/pipelineColors.ts for how the two share one colour vocabulary.
export type Outcome = 'reached' | 'booked' | 'noans' | 'none'

// Stages hidden from the Focus queue by default. The BDR can opt them back
// in via the Focus filter — must mirror backend `CLOSED_STAGES`.
// Queue bands, mirroring services/crm/lead/queries.py. The server decides which
// band a lead is in and returns it as `queueBand`; this side only labels and
// counts them, so the two can never disagree about what comes first.
export const BAND = { DUE: 0, SOON: 1, FRESH: 2, WAITING: 3 } as const
export type QueueBand = (typeof BAND)[keyof typeof BAND]

export const BAND_LABEL: Record<number, string> = {
  0: 'Ring nu',
  1: 'Lovat idag',
  2: 'Obearbetad',
  3: 'Väntar till senare datum',
}

export const BAND_HINT: Record<number, string> = {
  0: 'Tiden du lovade har passerat. Utlovade återuppringningar kommer först.',
  1: 'Utlovat senare idag — närmast först.',
  2: 'Aldrig bearbetad. Redo för ett första samtal.',
  3: 'Utlovat imorgon eller senare, så inte med i dagens lista. Går fortfarande att söka fram i Tabell.',
}

export const CLOSED_STAGES = ['Booked', 'Handed Over', 'Disqualified', 'Existing Customer', 'Wrong Contact'] as const

// Timeline kinds that count as "somebody has worked this lead". Excludes the
// bookkeeping kinds (stage, handover, created, primary_changed) that the system
// writes on its own. Read by the last-activity line, the release guard, and the
// handover card, which must all agree on what "worked" means.
export const INTERACTION_KINDS: ReadonlySet<string> = new Set([
  'call', 'note', 'email', 'meeting',
])

export const RATING_TIER = ['', 'Ingen match', 'Svag', 'Hyfsad', 'Stark', 'Het lead'] as const

// Canned reasons surfaced as one-tap chips above the rating comment field.
// Clicking a chip appends the phrase to the textarea (multi-select friendly).
// 'positive' = used for Booked / generic rating, 'notfit' = used for Not a Fit.
export const RATING_REASONS: { positive: readonly string[]; notfit: readonly string[] } = {
  positive: [
    'Beslutsfattare engagerad',
    'Aktivt behov',
    'Budget bekräftad',
    'Rätt tajming',
    'Bra match',
    'Vill jämföra alternativ',
    'Champion identifierad',
    'Bad om offert',
  ],
  notfit: [
    'Fel storlek',
    'Fel bransch',
    'Ingen budget',
    'Har redan en lösning',
    'Inte köpare',
    'Fel tillfälle',
    'Inte intresserad',
    'Utanför vår marknad',
  ],
} as const

// Rating is only meaningful at the inflection points — Booked (lead is leaving
// the BDR's queue forward, rate the quality going in) and Not a Fit (lead is
// leaving for good, rate the signal). On Reached / No Answer the cycle continues
// and no additional rating is needed.
export const OUTCOMES = {
  reached:     { label: 'Kontakt',          stage: 'Contacted' as Stage,         patch: { gotContact: true },    askRating: false, askNext: true,  defaultDays: 7,  nextLabel: undefined,        eventKind: 'call'  as const },
  // Somebody answered and pointed you at someone else ("you want our office
  // manager, that's Hampus"). Contact was made, so the lead is Contacted and
  // counts as reached — what changed is who to call, and that person is added
  // to the lead as the new primary before this outcome is logged. Comes back
  // tomorrow: the referral is warm and the hand-off shouldn't go cold.
  // `needsCapture`: the outcome is meaningless without fields the plain outcome
  // sheet does not collect — here, who answered and who they sent you to. A
  // surface that cannot ask for them must not offer the outcome, or it records
  // a referral to nobody.
  referred:    { label: 'Hänvisad vidare',  stage: 'Contacted' as Stage,         patch: { gotContact: true },    askRating: false, askNext: true,  defaultDays: 1,  nextLabel: 'Ring hänvisningen', eventKind: 'call'  as const, needsCapture: true },
  voicemail:   { label: 'Inget svar',       stage: null,                          patch: {},                      askRating: false, askNext: true,  defaultDays: 1,  nextLabel: undefined,        eventKind: 'call'  as const },
  booked:      { label: 'Bokat möte',       stage: 'Booked' as Stage,            patch: { bookedMeeting: true }, askRating: true,  askNext: true,  defaultDays: 0,  nextLabel: 'Mötesdatum',     eventKind: 'call'  as const },
  notfit:      { label: 'Ej aktuell',       stage: 'Disqualified' as Stage,      patch: {},                      askRating: true,  askNext: false, defaultDays: 0,  nextLabel: undefined,        eventKind: 'call'  as const },
  // Already a customer / active prospect — preserves the lead's data for
  // analytics (we still want to learn from their signals) but removes them
  // from the active call queue.  Distinct stage so it doesn't muddle the
  // "not a fit" bucket in reporting.
  customer:    { label: 'Redan kund',       stage: 'Existing Customer' as Stage, patch: {},                      askRating: false, askNext: false, defaultDays: 0,  nextLabel: undefined,        eventKind: 'call'  as const },
  // Bad contact info — number disconnected, contact left the company,
  // wrong person etc. Don't disqualify the company; park it in
  // 'Wrong Contact' so sales can revisit when they have time to look up
  // a new contact / add a phone number. Preserves the lead's data and
  // signals for the eventual retry.
  bad_contact: { label: 'Fel kontakt',      stage: 'Wrong Contact' as Stage,     patch: {},                      askRating: false, askNext: false, defaultDays: 0,  nextLabel: undefined,        eventKind: 'call'  as const },
  // Sent an email — soft touch. Doesn't bump stage; just parks the lead off
  // the focus queue until the follow-up date (default 1 week). Logs as an
  // 'email' timeline event rather than a 'call' so the chat reads correctly.
  email_sent:  { label: 'Mejl skickat',     stage: null,                          patch: {},                      askRating: false, askNext: true,  defaultDays: 7,  nextLabel: 'Följ upp', eventKind: 'email' as const },
} as const

export type OutcomeKind = keyof typeof OUTCOMES

// Which `askNext` outcomes need a date the BDR actually picked, and which may
// fall back to `defaultDays`. An outcome that leaves the lead in an open stage
// must end up with *some* next date — without one the lead is neither parked
// nor closed, so the queue keeps handing it back. Those fall back silently.
// Outcomes landing in a closed stage are already out of the queue and their
// date means something concrete (Booked → the meeting itself), so a guessed
// date would be a lie: require a real one instead.
export const requiresExplicitNextDate = (kind: OutcomeKind): boolean => {
  const { askNext, stage } = OUTCOMES[kind]
  return askNext && !!stage && (CLOSED_STAGES as readonly string[]).includes(stage)
}

function relativeDaysPhrase(days: number): string {
  if (days <= 0) return 'idag'
  if (days === 1) return 'imorgon'
  if (days % 7 === 0) {
    const weeks = days / 7
    return `om ${weeks} vecka${weeks > 1 ? 'r' : ''}`
  }
  return `om ${days} dagar`
}

// A short, concrete phrase for "what happens next" if this outcome is saved —
// read off the same fields `useOutcomeFlow` saves from (askNext, defaultDays,
// requiresExplicitNextDate), so the outcome buttons can never promise a
// follow-up the save logic doesn't actually schedule.
export function outcomeNextStep(kind: OutcomeKind): string {
  const def = OUTCOMES[kind]
  if (!def.askNext) return 'stänger leaden'
  if (requiresExplicitNextDate(kind)) return 'välj en tid'
  if ('needsCapture' in def && def.needsCapture) return 'ring hänvisningen imorgon'
  return `tillbaka ${relativeDaysPhrase(def.defaultDays)}`
}

// Salesforce statuses that keep a lead out of the focus queue: Salesforce owns
// that relationship, so a BDR must not ring it. The values are the ones
// Salesforce actually stores, which are Swedish — the picklist *labels* are
// English, and this list read ['Existing Customer'] on both sides for as long
// as the rule existed, matching nothing at all.
//
// Mirrors the config value BLOCKING_SF_LEAD_STATUSES (api/shared/settings.py),
// kept in step with its default by a test that reads this file, because the
// client re-filters the list the server already filtered and a disagreement
// shows up as a lead flickering in and out of the queue.
export const EXCLUDED_SF_STATUSES: readonly string[] = [
  'Konverterad',
  'Arbetar',
  'Kvalificerad/Intressant',
]
